// @ts-check
// workspaces.js — "Workspaces" settings section.
//
// Extracted from settings-panel.js (Stage D of the monolith split). The
// deepest-reaching section: the workspace list (switch / manage), per-
// workspace rename + colour + members + delete/leave, the member roster with
// role changes + invitations, and workspace creation (local + shared).
//
// Owns the per-workspace members cache. The host's _wire clears it on the
// workspaces:changed event via the exported clearMembersCache() hook.
// renderWorkspaces(ctx) and the handlers that re-render take the shared ctx
// ({ row, rawRow, openExpansions, invalidate, isOpened }).

import { getNoteStorage, getSyncHelpers } from '../../storage/session.js';
import { confirmStatus } from '../../ui/status-pill.js';
import { WorkspaceManager } from '../../workspaces/workspace-manager.js';
import { WorkspaceStore } from '../../workspaces/workspace-store.js';
import { toggleMenu } from '../../ui/menu-popover.js';
import { openWorkspaceMenu } from '../../workspaces/workspace-switch-menu.js';
import { WorkspaceSwitcher } from '../../workspaces/workspace-switcher.js';
import { WorkspaceAvatar } from '../../workspaces/workspace-toolbar-avatar.js';
import type { SettingsSectionCtx } from '../settings-section-api.js';
import {
  addCta,
  buildRevealToggle,
  colorPicker,
  confirm,
  makeP,
  prompt,
  removeButton,
  syncRevealIcon,
  toast,
} from './_common.js';

/**
 * @param {any} ctx - the section render context.
 * @returns {any[]} the section's rows.
 */
export function renderWorkspaces(ctx: SettingsSectionCtx) {
  const rows = [];
  /** @type {any[]} */
  const list = WorkspaceStore?.list?.() || [];
  const activeId = WorkspaceStore?.getActiveId?.() || null;
  const active = list.find((w: any) => w.id === activeId);

  // Resolve the theme-accent fallback (a getComputedStyle read) at most once per
  // render pass, not once per uncoloured workspace on every reconcile. A theme
  // change routes through the host's full _renderAll (which reruns this
  // function), so the memo is recaptured whenever --accent could actually move.
  let _accentOnce: string | undefined;
  const accentFallback = () => (_accentOnce ??= _accentHex());

  const GROUP = 'group:workspaces';
  rows.push(
    ctx.row({
      id: GROUP,
      label: 'Workspaces',
      // The active workspace name in the heading is the SWITCHER: clicking it
      // toggles to the next workspace (a switch reloads, by design). The rows
      // below are management-only and never switch on click.
      control: buildHeadingSwitcher,
      updateControl: refreshHeadingSwitcher,
      expandable: true,
      searchExpand: true,
      cls: 'settings-row-group',
      searchText: `workspaces switch ${active?.name || ''} ${list.map((w: any) => `${w.name} ${w.supabaseEmail || ''}`).join(' ')}`,
    })
  );
  if (!ctx.openExpansions.has(GROUP)) return rows;

  // "Focus mode": while a workspace's members panel is open, show ONLY that
  // workspace + its panel — hide the other workspace rows and the "New
  // workspace" button so the roster/invite has the section to itself. They all
  // reappear once the members panel is toggled off.
  // Members panels are server-backed, so they only exist while signed in — ignore
  // any stale open-state when signed out (e.g. a session that expired mid-view).
  const authed = !!getSyncHelpers()?.authenticated;
  const anyMembersOpen =
    authed && list.some((w: any) => ctx.openExpansions.has(`workspace:${w.id}`));

  for (const w of list) {
    const ID = `workspace:${w.id}`;
    const isActive = w.id === activeId;
    const isShared = w.kind === 'shared' && !!w.workspaceId;
    const open = ctx.openExpansions.has(ID);
    if (anyMembersOpen && !open) continue; // hide the non-focused workspaces

    // Each row is management-only — name (inline-edit), colour (the dot IS the
    // picker), and a trailing "×" delete. Clicking a row NEVER switches; that's
    // the heading switcher's job. Shared workspaces stay expandable for the
    // member roster (fold via the accent line / squiggle).
    const commitRename = async (name: string) => {
      const next = (name || '').trim();
      if (!next || next === w.name) {
        ctx.invalidate(); // restore the displayed label
        return;
      }
      try {
        await renameWorkspace(ctx, w, next);
      } catch (e: any) {
        console.warn('[settings] rename failed:', e);
        ctx.invalidate();
      }
    };

    rows.push(
      ctx.row({
        id: ID,
        label: w.name,
        cls: 'settings-row-workspace',
        indent: true,
        // NOT a fold group — the members icon is the only reveal/collapse
        // affordance, so the row shows no fold squiggle and its member panel
        // carries no per-workspace vertical accent line. (Members still emit
        // below via `if (isShared && open)`, driven by ctx.openExpansions.)
        expandable: false,
        active: isActive,
        // [colour picker][× delete]. The colour dot is the live picker; the "×"
        // deletes (or leaves) the workspace after a toast confirm, and is greyed
        // out for the active workspace (you can't delete the one you're in).
        control: () => buildWorkspaceControls(ctx, w, isActive),
        updateControl: (el: any) => {
          const picker = el.querySelector('input[type="color"]') as HTMLInputElement | null;
          if (picker) {
            picker.value = w.color || accentFallback();
            picker.setAttribute('aria-label', `${w.name} colour`);
          }
          const memBtn = el.querySelector('.settings-ws-members') as HTMLElement | null;
          if (memBtn && w.kind === 'shared' && w.workspaceId) {
            syncMembersButton(memBtn, ctx.openExpansions.has(ID), w.name);
          }
        },
        editable: { onCommit: commitRename, ariaLabel: 'Workspace name' },
        searchText: `workspace ${w.name} ${w.supabaseEmail || ''}`,
      })
    );

    if (isShared && open && authed) rows.push(...renderWorkspaceManage(ctx, w));
  }

  // One "＋ New workspace" button (the two separate local / shared buttons are
  // merged). Signed in it creates a synced workspace — the only kind that can
  // have members, invited via the row's members icon; signed out it creates a
  // local sandbox workspace, edited inline. Full-width primary (same as Sync's
  // "Send code" CTA) via the `.settings-form > .btn:not(.btn--icon)` width rule.
  // Hidden while a members panel is focused (see anyMembersOpen).
  if (!anyMembersOpen) {
    const authed = !!getSyncHelpers()?.authenticated;
    rows.push(
      ctx.rawRow(() => addCta('＋ New workspace', () => createWorkspace(ctx, authed)), {
        key: 'workspace:add',
        indent: true,
        searchText: 'workspace new add create shared synced',
      })
    );
  }

  return rows;
}

// Per-workspace manage panel — now ONLY the member roster (shared workspaces).
// Name + colour are inline on the row, and delete/leave is the row's "×", so
// this is reached only for shared workspaces and only holds Members.
/**
 * @param {any} ctx
 * @param {any} w - the workspace entry.
 * @returns {any[]} the manage-panel rows.
 */
function renderWorkspaceManage(ctx: SettingsSectionCtx, w: any) {
  const rows = [];
  if (w.kind === 'shared' && w.workspaceId) {
    // The workspace row is already expanded to get here (via the members icon or
    // the fold line), so show the roster + invite directly — no intermediate
    // "Members: N" sub-fold.
    ensureMembersLoaded(ctx, w.workspaceId);
    rows.push(...renderMembers(ctx, w, _membersCache.get(w.workspaceId)));
  }
  return rows;
}

// ── Members (per shared workspace) ───────────────────────────────────
// Keyed by workspaceId. Lazy-loaded when the Members row is first
// expanded; cleared on a workspaces:changed event so role/invite
// changes elsewhere are picked up.
const _membersCache = new Map();
const _membersLoading = new Set();

// Called by the host's workspaces:changed listener so role/invite changes
// made elsewhere are re-fetched on the next Members expand.
/** @returns {void} */
export function clearMembersCache() {
  _membersCache.clear();
}

/**
 * @param {any} ctx
 * @param {any} wsId - the workspace id.
 * @returns {Promise<void>}
 */
async function ensureMembersLoaded(ctx: SettingsSectionCtx, wsId: any) {
  if (_membersCache.has(wsId) || _membersLoading.has(wsId)) return;
  if (!WorkspaceManager?.listMembers) return;
  _membersLoading.add(wsId);
  try {
    const [members, invites] = await Promise.all([
      WorkspaceManager.listMembers(wsId).catch(() => [] as any),
      WorkspaceManager.listInvitations?.(wsId).catch(() => [] as any) || [],
    ]);
    _membersCache.set(wsId, {
      members: members || [],
      invitations: (invites || []).filter(
        (/** @type {any} */ i: any) => !i.accepted_at && !i.revoked_at
      ),
    });
  } finally {
    _membersLoading.delete(wsId);
  }
  if (ctx.isOpened()) ctx.invalidate();
}

/**
 * @param {any} ctx
 * @param {any} w - the workspace entry.
 * @param {any} info - cached { members, invitations }, or undefined.
 * @returns {any[]} the member-roster rows.
 */
function renderMembers(ctx: SettingsSectionCtx, w: any, info: any) {
  const rows = [];
  // "Members" subheading — always present, so the expanded group keeps ≥1 child
  // (the collapse line) even while the roster is still loading or empty.
  rows.push(
    ctx.rawRow(() => makeP('Members'), {
      key: `ws-members-head:${w.workspaceId}`,
      indent: 1,
      cls: 'settings-row-subhead settings-ws-member',
      searchText: `members ${w.name}`,
    })
  );
  if (!info) {
    rows.push(
      ctx.rawRow(() => makeP('Loading…'), {
        key: `ws-members-loading:${w.workspaceId}`,
        indent: 1,
        cls: 'settings-ws-member',
      })
    );
    return rows;
  }
  const isAdmin = w.role === 'admin';
  for (const m of info.members) {
    // Key includes the role so a role change remounts the row (the select goes
    // through a blur/commit, so no focused-input is torn out).
    rows.push(
      ctx.rawRow(() => buildMemberRow(ctx, w, m, isAdmin), {
        key: `ws-member:${w.workspaceId}:${m.user_id || m.email}:${m.role}`,
        indent: 1,
        cls: 'settings-ws-member',
        searchText: `member ${m.email} ${m.role}`,
      })
    );
  }

  if (info.invitations.length) {
    rows.push(
      ctx.rawRow(() => makeP('Pending invitations'), {
        key: `ws-invites-head:${w.workspaceId}`,
        indent: 1,
        cls: 'settings-row-subhead settings-ws-member',
        searchText: 'pending invitations',
      })
    );
    for (const inv of info.invitations) {
      rows.push(
        ctx.rawRow(() => buildInvitationRow(ctx, w, inv, isAdmin), {
          key: `ws-invite:${w.workspaceId}:${inv.id}`,
          indent: 1,
          cls: 'settings-ws-member',
          searchText: `invitation ${inv.email} ${inv.role}`,
        })
      );
    }
  }

  if (isAdmin && typeof WorkspaceManager?.invite === 'function') {
    // "Invite" subheading + the invite form.
    rows.push(
      ctx.rawRow(() => makeP('Invite'), {
        key: `ws-invite-head:${w.workspaceId}`,
        indent: 1,
        cls: 'settings-row-subhead settings-ws-member',
        searchText: 'invite member email role',
      })
    );
    rows.push(
      ctx.rawRow(() => buildInviteForm(ctx, w), {
        key: `ws-invite-form:${w.workspaceId}`,
        indent: 1,
        cls: 'settings-ws-member',
        searchText: 'invite member email role',
      })
    );
  }

  return rows;
}

// ── Heading switcher (the active-workspace name = "open switcher dropdown") ──

// The Workspaces heading's right-hand control: the active workspace name, as a
// button that opens a dropdown of ALL workspaces to switch to (a switch reloads
// the app to re-init the workspace namespace). Reads the store fresh on click so
// it's never a stale closure.
function buildHeadingSwitcher(): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'settings-value-btn';
  refreshHeadingSwitcher(btn);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (btn.disabled) return;
    toggleMenu(btn, () => openWorkspaceMenu(btn));
  });
  return btn;
}

function refreshHeadingSwitcher(el: HTMLElement): void {
  const btn = el as HTMLButtonElement;
  const list = WorkspaceStore?.list?.() || [];
  const activeId = WorkspaceStore?.getActiveId?.() || null;
  const active = list.find((w: any) => w.id === activeId);
  btn.textContent = active?.name || '–';
  const multi = list.length > 1;
  // The caret marks it as a dropdown; with a single workspace there's nothing to
  // switch to, so it's inert (no caret, not clickable).
  btn.classList.toggle('settings-value-btn-menu', multi);
  btn.disabled = !multi;
  btn.title = multi ? 'Switch workspace' : '';
  btn.setAttribute('aria-haspopup', multi ? 'menu' : 'false');
  btn.setAttribute(
    'aria-label',
    multi ? `Workspace ${active?.name || ''}; switch` : `Workspace ${active?.name || ''}`
  );
}

// ── Row controls: [colour picker][× delete] ─────────────────────────────────

// The workspace row's right-hand controls: the colour picker + a "×" that
// deletes (local) or leaves (shared) the workspace after a toast confirm. The
// "×" is greyed out for the active workspace — you can't delete the one you're
// in (switch away first).
function buildWorkspaceControls(ctx: SettingsSectionCtx, w: any, isActive: any): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'settings-row-control';
  // The colour swatch (18px) sits in a 28px slot so its centre lines up with the
  // members + delete icon buttons (also 28px) — otherwise its narrower box pulls
  // its centre left and the three controls read as unevenly spaced.
  const colorSlot = document.createElement('span');
  colorSlot.className = 'settings-ctrl-slot';
  colorSlot.appendChild(buildWorkspaceColorPicker(ctx, w));
  wrap.appendChild(colorSlot);
  // Members icon — always between the colour picker and the "×". Shared
  // workspaces toggle their roster / invite panel; non-shared ones show it
  // grayed, and clicking offers to convert the workspace to shared (so members
  // can be added).
  wrap.appendChild(buildMembersButton(ctx, w));
  // Cloud link/unlink — only on account-linkable entries (local sandbox) or
  // linked personal entries; null (skipped) for shared/other rows.
  const linkBtn = buildLinkButton(w);
  if (linkBtn) wrap.appendChild(linkBtn);
  wrap.appendChild(buildDeleteButton(ctx, w, isActive));
  return wrap;
}

// Material "people" glyph (fill:currentColor so it inherits the icon button's
// muted→text colour, incl. the .is-open accent when the roster is showing).
const MEMBERS_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 ' +
  '3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0' +
  '-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v' +
  '-2.5c0-2.33-4.67-3.5-7-3.5z"/></svg>';

// The members icon button. Toggles the workspace row's expansion (its member
// roster + invite panel) — the discoverable twin of the editor-style fold line.
// Its open-state is patched by the row's updateControl (the control node is
// retained across re-renders), so reflect it on a stable class the patch reads.
function buildMembersButton(ctx: SettingsSectionCtx, w: any): HTMLElement {
  const ID = `workspace:${w.id}`;
  const isShared = w.kind === 'shared' && !!w.workspaceId;
  const authed = !!getSyncHelpers()?.authenticated;
  // Enabled shared roster: the same reveal-toggle shape as the calendar-list
  // icon — toggles the workspace row's member roster + invite panel.
  if (isShared && authed) {
    return buildRevealToggle(ctx, ID, {
      svg: MEMBERS_ICON,
      cls: 'settings-ws-members',
      title: 'Members',
      openLabel: `Hide members of ${w.name}`,
      closedLabel: `Members of ${w.name}`,
    });
  }
  // Disabled variants — a grayed icon that doesn't reveal a roster.
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn--icon btn--ghost settings-ws-members is-disabled';
  btn.innerHTML = MEMBERS_ICON;
  if (isShared) {
    // Shared workspace but signed out — its roster + invites are server-side, so
    // the entry lingers (cloud metadata isn't reverted on sign-out) but can't be
    // managed offline. Gray the icon and point to sign-in rather than opening a
    // roster that can't load.
    btn.setAttribute('aria-label', `Sign in to manage members of ${w.name}`);
    btn.title = 'Sign in to manage members';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      toast('Sign in to manage members of a shared workspace.', 'warning');
    });
  } else {
    // Non-shared: grayed. Clicking offers to convert the workspace to a shared
    // one so members can be added.
    btn.setAttribute('aria-label', `Share ${w.name} to add members`);
    btn.title = 'Convert to a shared workspace to add members';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      offerConvertToShared(w);
    });
  }
  return btn;
}

// Grayed members-icon click on a non-shared workspace: confirm, then convert it
// to a shared (cloud) workspace so members can be invited. Personal workspaces
// stay private; conversion needs a signed-in account.
function offerConvertToShared(w: any): void {
  (async () => {
    if (w.kind === 'personal') {
      toast('Your personal workspace stays private — it can’t be shared.', 'info');
      return;
    }
    if (!getSyncHelpers()?.authenticated) {
      toast('Sign in under Sync to share a workspace.', 'warning');
      return;
    }
    // Only read the note count when this IS the active workspace — NoteStorage
    // is active-scoped, so reading it for a non-active row would count the wrong
    // workspace. A non-active convert switches to the target first.
    const isActive = WorkspaceStore?.getActiveId?.() === w.id;
    let detail: string;
    if (isActive) {
      let noteCount = 0;
      try {
        noteCount = (((await getNoteStorage()?.getAllNotes?.()) || []) as any[]).length;
      } catch {
        /* count is informational */
      }
      detail =
        noteCount > 0
          ? ` Its ${noteCount} note${noteCount === 1 ? '' : 's'} and any attachments move to the cloud.`
          : '';
    } else {
      detail = ' You’ll switch to it, and its notes and attachments move to the cloud.';
    }
    const ok = await confirm({
      title: `Share “${w.name}”?`,
      message:
        'Convert this workspace to a shared cloud workspace so you can invite members.' +
        detail +
        ' This can’t be undone.',
      confirmLabel: 'Share',
    });
    if (!ok) return;
    try {
      await WorkspaceSwitcher?.convertSandboxToShared?.(w.id);
      // Success reloads the app into the now-shared workspace.
    } catch (e: any) {
      console.warn('[settings] convert to shared failed:', e);
      toast(e?.message || 'Could not convert workspace.', 'warning');
    }
  })();
}

// ── Cloud link / unlink (personal account sync) ────────────────────────────
// Cloud-upload (link) and cloud-off (unlink) glyphs, 15×15, currentColor.
const LINK_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 ' +
  '10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/></svg>';
const UNLINK_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M19.35 10.04C18.67 6.59 15.64 4 12 4c-1.48 0-2.85.43-4.01 1.17l1.46 1.46C10.21 ' +
  '6.23 11.08 6 12 6c3.04 0 5.5 2.46 5.5 5.5v.5H19c1.66 0 3 1.34 3 3 0 1.13-.64 2.11-1.56 ' +
  '2.62l1.45 1.45C23.16 18.16 24 16.68 24 15c0-2.64-2.05-4.78-4.65-4.96zM3 5.27l2.75 2.74C2.56 ' +
  '8.15 0 10.77 0 14c0 3.31 2.69 6 6 6h11.73l2 2L21 20.73 4.27 4 3 5.27zM7.73 10l8 8H6c-2.21 ' +
  '0-4-1.79-4-4s1.79-4 4-4h1.73z"/></svg>';

// Per-row cloud link/unlink affordance. A LOCAL sandbox shows "Link to account"
// (prompts for an email and sends a magic sign-in link via linkSupabase; on
// sign-in the entry becomes a synced personal workspace — see
// workspace-switcher._onSupabaseAuthChange). A linked personal entry shows
// "Disconnect", reverting it to a local sandbox via unlinkSupabase. Shared
// workspaces show neither (they're joined/left, not account-linked). Returns
// null when no affordance applies.
function buildLinkButton(w: any): HTMLElement | null {
  const canLink = w.kind === 'sandbox' && !w.supabaseEmail && !w.workspaceId;
  const canUnlink = w.kind === 'personal' && !!w.supabaseEmail;
  if (!canLink && !canUnlink) return null;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn--icon btn--ghost settings-ws-link';
  if (canLink) {
    btn.innerHTML = LINK_ICON;
    btn.title = 'Link to account';
    btn.setAttribute('aria-label', `Link “${w.name}” to your account`);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      offerLink(w);
    });
  } else {
    btn.innerHTML = UNLINK_ICON;
    btn.title = 'Disconnect from account';
    btn.setAttribute('aria-label', `Disconnect “${w.name}” from your account`);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      offerUnlink(w);
    });
  }
  return btn;
}

// Prompt for an account email and send a magic sign-in link that links this
// local workspace to the account (linkSupabase warns first if the sandbox has
// local-only notes that would fall out of view after the prefix changes).
function offerLink(w: any): void {
  (async () => {
    if (!getSyncHelpers()) {
      toast('Sync is unavailable — cannot link a workspace right now.', 'warning');
      return;
    }
    const email = await prompt({
      title: `Link “${w.name}” to an account`,
      message:
        'Enter your account email. We’ll send a sign-in link — open it to finish linking this workspace to the cloud.',
      placeholder: 'you@example.com',
      confirmLabel: 'Send link',
    });
    if (!email?.trim()) return;
    try {
      await WorkspaceSwitcher?.linkSupabase?.(w.id, email.trim());
      toast(
        `Sign-in link sent to ${email.trim()} — open it to finish linking “${w.name}”.`,
        'info'
      );
    } catch (e: any) {
      // linkSupabase throws "Link cancelled." when the user declines the
      // sandbox-notes-loss confirm — that's not an error to surface.
      if (/cancel/i.test(e?.message || '')) return;
      toast(e?.message || 'Could not send the sign-in link.', 'warning');
    }
  })();
}

// Confirm, then disconnect a linked personal workspace back to a local-only
// sandbox (unlinkSupabase). Unlinking the ACTIVE entry signs out of the account
// on this device and re-inits into local mode; a non-active unlink reverts the
// entry and broadcasts a workspaces:changed re-render.
function offerUnlink(w: any): void {
  (async () => {
    const isActive = WorkspaceStore?.getActiveId?.() === w.id;
    const ok = await confirm({
      title: `Disconnect “${w.name}” from your account?`,
      message:
        'This device stops syncing the workspace and it becomes a local-only sandbox. Your cloud copy is left untouched.' +
        (isActive ? ' You’ll be signed out of the account on this device.' : ''),
      confirmLabel: 'Disconnect',
    });
    if (!ok) return;
    try {
      await WorkspaceSwitcher?.unlinkSupabase?.(w.id);
      // Active unlink re-inits/reloads; a non-active unlink broadcasts a change
      // that re-renders the list. Toast only the quiet (non-active) case.
      if (!isActive) toast(`“${w.name}” is now local-only.`, 'info');
    } catch (e: any) {
      toast(e?.message || 'Could not disconnect the workspace.', 'warning');
    }
  })();
}

// Reflect open/closed state on the members button — shared by the initial build
// and the row's updateControl patch (the retained control isn't rebuilt).
function syncMembersButton(btn: HTMLElement, open: boolean, name: string): void {
  syncRevealIcon(btn, open, `Hide members of ${name}`, `Members of ${name}`);
}

function buildDeleteButton(ctx: SettingsSectionCtx, w: any, isActive: any): HTMLElement {
  const del = document.createElement('button');
  del.type = 'button';
  del.className = 'btn btn--icon btn--ghost btn--danger settings-ws-delete';
  del.textContent = '×';

  const canLeave = w.kind === 'shared' && w.role && w.role !== 'admin';
  // Admins can delete a shared workspace for everyone; sandbox/local entries are
  // deleted locally. Either way you must switch away first (the !isActive gate
  // below) — you can't delete the workspace you're currently sitting in.
  const canDeleteShared = w.kind === 'shared' && w.role === 'admin' && !!w.workspaceId;
  // A personal (account) workspace can't be DELETED here — its notes live in the
  // cloud and the account owns it — but a NON-active one can be FORGOTTEN: drop
  // this device's local copy + switcher row. Because the active personal entry IS
  // the signed-in account, any NON-active personal is always a different,
  // signed-out account left lingering by a plain sign-in (which spawns a fresh
  // personal entry rather than adopting your current workspace). Forgetting it is
  // local-only (no server call) — the notes stay in that account's cloud and
  // re-sync if you sign back in, and reconcile won't resurrect it (it's
  // RLS-invisible to the current session). This is the clean-up path for the
  // duplicate non-deletable entries.
  const canForgetPersonal = w.kind === 'personal';
  const canDelete =
    w.kind === 'sandbox' || w.kind === 'local' || canDeleteShared || canForgetPersonal;
  const deletable = !isActive && (canLeave || canDelete);
  if (!deletable) {
    del.disabled = true;
    del.setAttribute(
      'aria-label',
      isActive ? `Can't delete the active workspace` : `Can't delete ${w.name}`
    );
    del.title = isActive
      ? 'Switch to another workspace to delete this one'
      : 'This workspace can’t be deleted here';
    return del;
  }

  const verb = canLeave ? 'Leave' : canForgetPersonal ? 'Remove' : 'Delete';
  del.setAttribute('aria-label', `${verb} ${w.name}`);
  del.title = canDeleteShared
    ? 'Delete shared workspace for everyone'
    : canForgetPersonal
      ? 'Remove this account’s workspace from this device — its notes stay in the cloud'
      : `${verb} workspace`;
  del.addEventListener('click', async () => {
    // Leaving / deleting a SHARED workspace is a server operation, so it needs a
    // live account session. The entry can still show while signed out (its cloud
    // metadata lingers from before sign-out — only the ACTIVE workspace gets
    // reverted), so say "sign in" clearly instead of confirming and then failing
    // the RPC with an opaque "could not delete workspace".
    if ((canLeave || canDeleteShared) && !getSyncHelpers()?.authenticated) {
      toast(
        'Sign in to delete or leave a shared workspace — it lives in your account, not on this device.',
        'warning'
      );
      return;
    }
    // Deleting a shared workspace tears it down for every member — say so;
    // leaving / local-delete only affects this user or this device.
    const msg = canDeleteShared
      ? `Delete “${w.name}” for everyone? This permanently removes the shared workspace and all its notes for every member.`
      : canForgetPersonal
        ? `Remove “${w.name}” from this device? Its notes stay in your cloud account and re-sync if you sign in again.`
        : `${verb} “${w.name}”?`;
    const ok = await confirmStatus(msg, {
      kind: 'warning',
      confirmLabel: verb,
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    if (canLeave) leaveWorkspace(w);
    else if (canDeleteShared) deleteSharedWorkspace(ctx, w);
    else removeWorkspace(ctx, w);
  });
  return del;
}

// ── Manage-panel control builders (factories, built once + retained) ─────────

// The workspace row's colour swatch, as the native picker (styled to the 18px
// circle). Live preview tints the avatar; the commit persists on dialog close.
function buildWorkspaceColorPicker(ctx: SettingsSectionCtx, w: any): HTMLInputElement {
  return colorPicker(
    w.color || _accentHex(),
    (v: any, phase: any) => {
      if (phase === 'preview') {
        WorkspaceAvatar?.refresh?.();
        return;
      }
      setWorkspaceColor(ctx, w, v).catch((e) => console.warn('[settings] set color failed:', e));
    },
    `${w.name} colour`
  );
}

// A hex fallback for a workspace with no explicit colour — the live theme
// accent if it resolves to a hex (native colour inputs only take hex), else a
// neutral grey. Keeps an uncoloured workspace's swatch on-theme.
function _accentHex(): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim();
    return /^#[0-9a-f]{6}$/i.test(v) ? v : '#888888';
  } catch {
    return '#888888';
  }
}

function buildMemberRow(ctx: SettingsSectionCtx, w: any, m: any, isAdmin: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-device-row';
  const label = document.createElement('span');
  label.className = 'settings-row-text';
  label.textContent = m.email || m.user_id || 'member';
  wrap.appendChild(label);
  if (isAdmin && m.role !== 'admin') {
    const select = document.createElement('select');
    select.className = 'settings-mini-input';
    for (const r of ['viewer', 'editor', 'admin']) {
      const opt = document.createElement('option');
      opt.value = r;
      opt.textContent = r;
      if (m.role === r) opt.selected = true;
      select.appendChild(opt);
    }
    select.addEventListener('change', async () => {
      try {
        await WorkspaceManager.setMemberRole(w.workspaceId, m.user_id, select.value);
      } catch (e: any) {
        console.warn('[settings] setMemberRole failed:', e);
      }
      _membersCache.delete(w.workspaceId);
      ctx.invalidate();
    });
    const rm = removeButton({ ariaLabel: 'Remove member' });
    rm.addEventListener('click', async () => {
      if (
        !(await confirm({
          title: `Remove ${m.email}?`,
          message: 'They lose access to this workspace immediately.',
          confirmLabel: 'Remove',
          danger: true,
        }))
      )
        return;
      try {
        await WorkspaceManager.removeMember(w.workspaceId, m.user_id);
      } catch (e: any) {
        console.warn('[settings] removeMember failed:', e);
      }
      _membersCache.delete(w.workspaceId);
      ctx.invalidate();
    });
    wrap.append(select, rm);
  } else {
    const roleBadge = document.createElement('span');
    roleBadge.className = 'settings-row-value';
    roleBadge.textContent = m.role || '';
    wrap.appendChild(roleBadge);
  }
  return wrap;
}

function buildInvitationRow(ctx: SettingsSectionCtx, w: any, inv: any, isAdmin: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-device-row';
  const label = document.createElement('span');
  label.className = 'settings-row-text';
  label.textContent = `${inv.email} (${inv.role})`;
  wrap.appendChild(label);
  if (isAdmin) {
    if (inv.token && WorkspaceManager?.buildInviteUrl) {
      const copy = document.createElement('button');
      copy.type = 'button';
      copy.className = 'btn';
      copy.textContent = 'Copy link';
      copy.addEventListener('click', async () => {
        try {
          const url = WorkspaceManager.buildInviteUrl(inv.token);
          await navigator.clipboard.writeText(url);
          toast('Invite link copied.', 'info');
        } catch (e: any) {
          console.warn('[settings] copy invite failed:', e);
        }
      });
      wrap.appendChild(copy);
    }
    const rm = removeButton({ ariaLabel: 'Revoke invitation' });
    rm.addEventListener('click', async () => {
      try {
        await WorkspaceManager.revokeInvitation(inv.id);
      } catch (e: any) {
        console.warn('[settings] revokeInvitation failed:', e);
      }
      _membersCache.delete(w.workspaceId);
      ctx.invalidate();
    });
    wrap.appendChild(rm);
  }
  return wrap;
}

function buildInviteForm(ctx: SettingsSectionCtx, w: any): HTMLElement {
  const form = document.createElement('div');
  form.className = 'settings-form';
  const email = document.createElement('input');
  email.type = 'email';
  email.placeholder = 'invite@example.com';
  email.className = 'settings-mini-input';
  const role = document.createElement('select');
  role.className = 'settings-mini-input';
  for (const r of ['viewer', 'editor', 'admin']) {
    const opt = document.createElement('option');
    opt.value = r;
    opt.textContent = r;
    if (r === 'editor') opt.selected = true;
    role.appendChild(opt);
  }
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn btn--primary';
  btn.textContent = 'Invite';
  const submit = async () => {
    const e = (email.value || '').trim();
    if (!e) return;
    btn.disabled = true;
    try {
      const result = await WorkspaceManager.invite(w.workspaceId, e, role.value);
      if (result?.token && WorkspaceManager.buildInviteUrl) {
        const url = WorkspaceManager.buildInviteUrl(result.token);
        try {
          await navigator.clipboard.writeText(url);
          toast('Invite link copied.', 'info');
        } catch {
          toast('Invitation created.', 'info');
        }
      } else {
        toast('Invitation created.', 'info');
      }
      email.value = '';
      _membersCache.delete(w.workspaceId);
    } catch (err: any) {
      console.warn('[settings] invite failed:', err);
      toast(err?.message || 'Invite failed.', 'warning');
    }
    btn.disabled = false;
    ctx.invalidate();
  };
  btn.addEventListener('click', submit);
  email.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  });
  form.append(email, role, btn);
  return form;
}

/**
 * @param {any} ctx
 * @param {any} w
 * @param {string} name
 * @returns {Promise<void>}
 */
async function renameWorkspace(ctx: SettingsSectionCtx, w: any, name: any) {
  const mgr = WorkspaceManager;
  if (w.kind === 'shared' && mgr?.renameWorkspace) {
    await mgr.renameWorkspace(w.workspaceId || w.id, name);
  } else if (w.supabaseEmail && mgr?.setCustomName) {
    await mgr.setCustomName(w.workspaceId || w.id, name);
  } else {
    WorkspaceStore?.update?.(w.id, { name });
  }
  WorkspaceSwitcher?.reconcileWorkspaces?.();
  WorkspaceAvatar?.refresh?.();
  ctx.invalidate();
}

/**
 * @param {any} ctx
 * @param {any} w
 * @param {string} colour
 * @returns {Promise<void>}
 */
async function setWorkspaceColor(ctx: SettingsSectionCtx, w: any, colour: any) {
  const mgr = WorkspaceManager;
  if (mgr?.setColor) {
    try {
      await mgr.setColor(w.workspaceId || w.id, colour);
    } catch {
      /* fall through to local */
    }
  }
  WorkspaceStore?.update?.(w.id, { color: colour });
  WorkspaceAvatar?.refresh?.();
  ctx.invalidate();
}

/**
 * @param {any} ctx
 * @param {boolean} shared
 * @returns {void}
 */
function createWorkspace(ctx: SettingsSectionCtx, shared: any) {
  if (shared) {
    // Signed in: create a synced workspace and drop its row in place — same feel
    // as a local one (no modal, no toast, no switch/reload). It's created on the
    // server with a placeholder name; the inline edit on the new row renames it.
    (async () => {
      let ws: any;
      try {
        ws = await WorkspaceManager?.createWorkspace?.('Workspace');
      } catch (e: any) {
        console.warn('[settings] create shared failed:', e);
        return;
      }
      if (!ws?.id) return;
      const helpers = getSyncHelpers();
      const account = helpers?.userId
        ? { userId: helpers.userId, email: helpers.userEmail || null }
        : null;
      // create_workspace returns the workspaces row with no `role` column; the
      // creator is always admin, so stamp it so the new entry renders as a
      // manageable shared workspace (members icon, rename, delete).
      WorkspaceStore?.upsertFromWorkspace?.({ ...ws, role: 'admin' }, account);
      ctx.invalidate();
      const entry = WorkspaceStore?.findByWorkspace?.(ws.id);
      if (entry?.id) focusWorkspaceName(entry.id);
    })();
    return;
  }
  // Local: no prompt — just add a fresh row in place (no switch, no reload).
  // WorkspaceStore.create defaults a blank name to "Workspace" and doesn't
  // activate the new entry (there's already an active one), so nothing reloads.
  // Re-render, then focus + select the new row's inline name field so the user
  // can immediately type the real name over the placeholder.
  const entry = WorkspaceStore?.create?.({ name: '' });
  if (!entry) return;
  ctx.invalidate();
  focusWorkspaceName(entry.id);
}

// Focus + select the inline name field of a just-created workspace row, once
// the invalidate-driven re-render has mounted it.
function focusWorkspaceName(entryId: string) {
  requestAnimationFrame(() => {
    const el = document.querySelector(
      `#settingsList [data-row-id="workspace:${entryId}"] .settings-row-text[contenteditable]`
    ) as HTMLElement | null;
    if (!el) return;
    el.focus();
    const sel = window.getSelection?.();
    if (sel && el.firstChild) {
      const range = document.createRange();
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  });
}

/**
 * @param {any} w
 * @returns {void}
 */
// Confirmation is the caller's `confirmStatus` toast; this just does the leave.
function leaveWorkspace(w: any) {
  (async () => {
    const wasActive = WorkspaceStore?.getActiveId?.() === w.id;
    try {
      await WorkspaceManager?.leaveWorkspace?.(w.workspaceId || w.id);
    } catch (e: any) {
      console.warn('[settings] leave failed:', e);
      toast('Could not leave workspace.', 'warning');
      return;
    }
    // After leaving the ACTIVE workspace, its sync-stream filter no longer
    // matches and the local data is now stale — the user is stranded until
    // a manual reload. Recover by reconciling: the server list no longer
    // includes the left workspace, so reconcileWorkspaces prunes the stale
    // entry and bounces the active entry to the personal workspace (the same
    // recovery it performs when an admin removes us). For a non-active leave
    // there's nothing to bounce — just refresh the list.
    WorkspaceSwitcher?.reconcileWorkspaces?.();
    if (!wasActive) WorkspaceAvatar?.refresh?.();
    toast(`Left ${w.name}.`, 'info');
  })();
}

/**
 * Admin-only: delete a shared workspace server-side for ALL members, then drop
 * the local entry. Only reachable for a non-active shared workspace where we're
 * admin (see buildDeleteButton). Mirrors leaveWorkspace, but the RPC tears down
 * the whole workspace rather than just our membership.
 * @param {any} ctx
 * @param {any} w
 * @returns {void}
 */
// Confirmation is the caller's `confirmStatus` toast; this just does the delete.
function deleteSharedWorkspace(ctx: SettingsSectionCtx, w: any) {
  (async () => {
    try {
      await WorkspaceManager?.deleteWorkspace?.(w.workspaceId || w.id);
    } catch (e: any) {
      console.warn('[settings] delete shared failed:', e);
      toast('Could not delete workspace.', 'warning');
      return;
    }
    // The workspace (and every membership) is gone server-side. Drop the local
    // entry so the row disappears immediately, then reconcile to settle any
    // cross-device/cross-tab state. The button is disabled for the active
    // workspace, so this never prunes the entry we're sitting in.
    try {
      WorkspaceStore?.remove?.(w.id);
    } catch (e: any) {
      console.warn('[settings] local prune after delete failed:', e?.message);
    }
    WorkspaceSwitcher?.reconcileWorkspaces?.();
    WorkspaceAvatar?.refresh?.();
    ctx.invalidate();
    toast(`Deleted ${w.name}.`, 'info');
  })();
}

/**
 * @param {any} ctx
 * @param {any} w
 * @returns {void}
 */
// Confirmation is the caller's `confirmStatus` toast; this just does the delete.
function removeWorkspace(ctx: SettingsSectionCtx, w: any) {
  (async () => {
    try {
      await WorkspaceSwitcher?.removeWorkspace?.(w.id);
      ctx.invalidate();
      toast(`${w.kind === 'personal' ? 'Removed' : 'Deleted'} ${w.name}.`, 'info');
    } catch (e: any) {
      console.warn('[settings] remove failed:', e);
      toast('Could not delete workspace.', 'warning');
    }
  })();
}
